import React, { createContext, useState, useEffect } from 'react';
import shopifyClient from '../utils/ShopifyClient';
import withLoading from '../utils/WithLoading';

const CART_FIELDS = `
  id
  lines(first: 100) {
    edges {
      node {
        id
        quantity
        merchandise {
          ... on ProductVariant {
            id
            priceV2 {
              amount
              currencyCode
            }
            product {
              title
              handle
            }
          }
        }
        sellingPlanAllocation {
          sellingPlan {
            name
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
      }
    }
  }
  cost {
    totalAmount {
      amount
      currencyCode
    }
  }
`;

const defaultValues = {
  cart: null,
  loading: false,
  didJustAddToCart: false,
  setDidJustAddToCart: () => {},
  addVariantToCart: () => {},
  removeLineItems: () => {},
  updateLineItem: () => {},
  fetchProductById: () => {},
  fetchProductByHandle: () => {},
  fetchSellingPlans: () => {},
  createCheckout: () => {},
};

export const StoreContext = createContext(defaultValues);

const isBrowser = typeof window !== `undefined`;
const localStorageKey = `shopify_cart_id`;

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [didJustAddToCart, setDidJustAddToCart] = useState(false);

  const setCartItem = (cart) => {
    if (isBrowser) {
      localStorage.setItem(localStorageKey, cart.id);
    }
    setCart(cart);
  };

  useEffect(() => {
    const initializeCart = async () => {
      await withLoading(
        async () => {
          const existingCartID = isBrowser
            ? localStorage.getItem(localStorageKey)
            : null;

          if (existingCartID && existingCartID !== 'null') {
            try {
              const response = await shopifyClient.request(
                `query getCart($cartId: ID!) { cart(id: $cartId) { ${CART_FIELDS} } }`,
                { cartId: existingCartID },
              );

              if (response.cart) {
                setCartItem(response.cart);
                return;
              }
            } catch (e) {
              localStorage.setItem(localStorageKey, null);
            }
          }

          const response = await shopifyClient.request(
            `mutation createCart { cartCreate { cart { ${CART_FIELDS} } } }`,
          );
          setCartItem(response.cartCreate.cart);
        },
        () => setLoading(true),
        () => setLoading(false),
      );
    };

    initializeCart();
  }, []);

  const addVariantToCart = async (
    variantId,
    quantity,
    sellingPlanId = null,
  ) => {
    if (!cart || !cart.id) {
      console.error('Cart not initialized.');
      return;
    }

    const cartId = cart.id;
    const line = { merchandiseId: variantId, quantity };
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    await withLoading(
      async () => {
        const response = await shopifyClient.request(
          `mutation addToCart($cartId: ID!, $lines: [CartLineInput!]!) {
            cartLinesAdd(cartId: $cartId, lines: $lines) {
              cart { ${CART_FIELDS} }
            }
          }`,
          { cartId, lines: [line] },
        );

        if (response.cartLinesAdd?.cart) {
          setCart(response.cartLinesAdd.cart);
          setDidJustAddToCart(true);
          setTimeout(() => setDidJustAddToCart(false), 3000);
        } else {
          console.error('Failed to add line item:', response);
        }
      },
      () => setLoading(true),
      () => setLoading(false),
    );
  };

  const removeLineItems = async (lineItemId) => {
    if (!cart || !cart.id) {
      console.error('Cart not initialized.');
      return;
    }

    const cartId = cart.id;
    const lineItemIds = Array.isArray(lineItemId) ? lineItemId : [lineItemId];

    await withLoading(
      async () => {
        const response = await shopifyClient.request(
          `mutation removeLineItems($cartId: ID!, $lineItemIds: [ID!]!) {
            cartLinesRemove(cartId: $cartId, lineIds: $lineItemIds) {
              cart { ${CART_FIELDS} }
            }
          }`,
          { cartId, lineItemIds },
        );

        if (response.cartLinesRemove?.cart) {
          setCart(response.cartLinesRemove.cart);
        } else {
          console.error('Failed to remove line items:', response);
        }
      },
      () => setLoading(true),
      () => setLoading(false),
    );
  };

  const updateLineItem = async (lineItemId, quantity) => {
    const cartId = cart.id;

    await withLoading(
      async () => {
        const response = await shopifyClient.request(
          `mutation updateLineItem($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
            cartLinesUpdate(cartId: $cartId, lines: $lines) {
              cart { ${CART_FIELDS} }
            }
          }`,
          { cartId, lines: [{ id: lineItemId, quantity }] },
        );
        setCart(response.cartLinesUpdate.cart);
      },
      () => setLoading(true),
      () => setLoading(false),
    );
  };

  const fetchProductById = async (id) => {
    const query = `
      query getProduct($id: ID!) {
        product(id: $id) {
          id
          title
          variants(first: 99) {
            edges {
              node {
                id
                priceV2 {
                  amount
                  currencyCode
                }
                product {
                  title
                  handle
                }
                availableForSale
                quantityAvailable
              }
            }
          }
        }
      }
    `;

    try {
      const response = await shopifyClient.request(query, { id });
      return response.product;
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  };

  const fetchProductByHandle = async (handle) => {
    const query = `
      query getProductByHandle($handle: String!) {
        productByHandle(handle: $handle) {
          id
          title
          handle
          variants(first: 99) {
            edges {
              node {
                id
                priceV2 {
                  amount
                  currencyCode
                }
                product {
                  title
                  handle
                }
                availableForSale
                quantityAvailable
              }
            }
          }
        }
      }
    `;

    try {
      const response = await shopifyClient.request(query, { handle });
      return response.productByHandle
        ? {
            ...response.productByHandle,
            variants: response.productByHandle.variants.edges.map(
              (edge) => edge.node,
            ),
          }
        : null;
    } catch (error) {
      console.error('Error fetching product by handle:', error);
      return null;
    }
  };

  const fetchSellingPlans = async (handle) => {
    const query = `
      query getSellingPlans($handle: String!) {
        productByHandle(handle: $handle) {
          variants(first: 1) {
            edges {
              node {
                sellingPlanAllocations(first: 10) {
                  edges {
                    node {
                      sellingPlan {
                        id
                        name
                        options {
                          name
                          value
                        }
                      }
                      priceAdjustments {
                        price {
                          amount
                          currencyCode
                        }
                        perDeliveryPrice {
                          amount
                          currencyCode
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await shopifyClient.request(query, { handle });
      const variant = response.productByHandle?.variants?.edges?.[0]?.node;
      const allocations = variant?.sellingPlanAllocations?.edges || [];
      return allocations.map(({ node }) => ({
        id: node.sellingPlan.id,
        name: node.sellingPlan.name,
        frequency: node.sellingPlan.options?.[0]?.value || '',
        price:
          node.priceAdjustments?.[0]?.perDeliveryPrice ||
          node.priceAdjustments?.[0]?.price,
      }));
    } catch (error) {
      console.error('Error fetching selling plans:', error);
      return [];
    }
  };

  const createCheckout = async () => {
    if (!cart || !cart.id) {
      console.error('Cart not initialized.');
      return null;
    }

    try {
      const response = await shopifyClient.request(
        `query getCartCheckoutUrl($cartId: ID!) { cart(id: $cartId) { checkoutUrl } }`,
        { cartId: cart.id },
      );
      return response.cart?.checkoutUrl || null;
    } catch (error) {
      console.error('Error getting checkout URL:', error);
      return null;
    }
  };

  return (
    <StoreContext.Provider
      value={{
        ...defaultValues,
        cart,
        loading,
        didJustAddToCart,
        addVariantToCart,
        removeLineItems,
        updateLineItem,
        fetchProductById,
        fetchProductByHandle,
        fetchSellingPlans,
        createCheckout,
        setDidJustAddToCart,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export default StoreContext;
